import {uniqueId} from "@/shared/helpers/string.js";

const getOrCreateToastContainer = () => {
    let container = document.getElementById('toast-container');
    if (!container) {
        container = document.createElement('div');
        container.id = 'toast-container';
        container.className = 'fixed z-50 pointer-events-none top-20 right-4';
        document.body.appendChild(container);
    }
    return container;
}

/**
 * Show a toast notification.
 *
 * @param {string} message - The message to display inside the toast.
 * @param {'success' | 'error' | 'info'| 'warning'| 'none'} type - The type of the toast which determines its color scheme.
 * @param {Object} [options] - Optional configuration for the toast.
 * @param {number} [options.duration=3000] - Duration in milliseconds before the toast disappears. If not set, then by click
 * @param {(() => Promise<void>)|null} [options.action]
 * @param {(() => Promise<void>)|null} [options.onClose]
 *
 * @returns {{toast: HTMLElement, close: () => void, show: () => void}}
 */
export const createNewToast = (message, type = 'info', options = {}) => {
    const {
        duration = 0,
        action = null,
        onClose = null
    } = options;

    const id = 'toast_' + uniqueId();

    const toast = document.createElement('div');
    toast.id = id;
    toast.className = "relative flex items-start w-full max-w-xs py-4 pl-4 pr-10 mb-3 rounded-lg min-w-lg" +
        " shadow-lg transition-all duration-300 opacity-0 pointer-events-auto";
    toast.role = "alert";
    toast.style = "min-width: 20rem";

    const close = (runCallback = true) => {
        toast.classList.remove('opacity-100');
        toast.classList.add('opacity-0');
        setTimeout(() => {
            if (toast.parentNode) {
                toast.remove();
            }
        }, 300);

        if (runCallback && onClose) {
            onClose();
        }
    }

    const backgroundClasses = {
        success: 'bg-green-100 text-green-900 border border-green-200',
        error: 'bg-red-100 text-red-900 border border-red-200',
        info: 'bg-blue-100 text-blue-900 border border-blue-200',
        warning: 'bg-amber-100 text-amber-900 border border-amber-200',
        none: 'bg-white text-gray-700 border border-gray-200'
    };

    const bgClass = backgroundClasses[type] || backgroundClasses['none'];
    toast.className = toast.className + ' ' + bgClass;

    const iconClasses = {
        success: 'text-white bg-green-500',
        error: 'text-white bg-red-500',
        info: 'text-white bg-blue-500',
        warning: 'text-white bg-amber-500'
    }

    const icons = {
        success: '<svg class="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20"><path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"/></svg>',
        error: '<svg class="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20"><path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 11.793a1 1 0 1 1-1.414 1.414L10 11.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L8.586 10 6.293 7.707a1 1 0 0 1 1.414-1.414L10 8.586l2.293-2.293a1 1 0 0 1 1.414 1.414L11.414 10l2.293 2.293Z"/></svg>',
        warning: '<svg class="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20"><path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM10 15a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm1-4a1 1 0 0 1-2 0V6a1 1 0 0 1 2 0v5Z"/></svg>',
        info: '<svg class="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24"><path fill-rule="evenodd" d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm9.408-5.5a1 1 0 1 0 0 2h.01a1 1 0 1 0 0-2h-.01ZM10 10a1 1 0 1 0 0 2h1v3h-1a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2h-1v-4a1 1 0 0 0-1-1h-2Z" clip-rule="evenodd"/></svg>'
    }

    // Крестик в правом верхнем углу
    const closeButtonClasses = {
        success: 'text-green-600 hover:text-green-800 hover:bg-green-200 focus:ring-green-300',
        error: 'text-red-600 hover:text-red-800 hover:bg-red-200 focus:ring-red-300',
        info: 'text-blue-600 hover:text-blue-800 hover:bg-blue-200 focus:ring-blue-300',
        warning: 'text-amber-600 hover:text-amber-800 hover:bg-amber-200 focus:ring-amber-300',
        none: 'text-gray-600 hover:text-gray-800 hover:bg-gray-100 focus:ring-gray-300'
    };

    const closeButton = document.createElement('button');
    closeButton.type = "button";
    closeButton.className = `absolute top-2 right-2 rounded-lg focus:ring-2 p-1 inline-flex items-center justify-center h-6 w-6 transition-colors ${closeButtonClasses[type] || closeButtonClasses['none']}`;
    closeButton.ariaLabel = "Close";
    closeButton.innerHTML = '<svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/></svg>';
    closeButton.addEventListener('click', (e) => {
        e.stopPropagation();
        close();
    });

    toast.appendChild(closeButton);

    // Контейнер для иконки и текста
    const contentContainer = document.createElement('div');
    contentContainer.className = "flex items-center";

    if (icons[type] && iconClasses[type]) {
        const iconContainer = document.createElement('div');
        iconContainer.className = `inline-flex items-center justify-center shrink-0 w-8 h-8 rounded-lg ${iconClasses[type]} mr-3 self-start`
        iconContainer.innerHTML = icons[type];
        contentContainer.appendChild(iconContainer);
    }

    const textContainer = document.createElement('div');
    textContainer.className = "text-sm font-normal flex-1";
    textContainer.innerHTML = message;
    contentContainer.appendChild(textContainer);

    toast.appendChild(contentContainer);

    if (action) {
        toast.addEventListener('click', (e) => {
            // Проверяем, что клик не по кнопке закрытия
            if (!e.target.closest('button')) {
                action();
                close(false);
            }
        });
        toast.classList.add('cursor-pointer');
    }

    const show = () => {
        const toastsContainer = getOrCreateToastContainer();

        toastsContainer.appendChild(toast);

        requestAnimationFrame(() => {
            toast.classList.remove('opacity-0');
            toast.classList.add('opacity-100');
        });

        if (duration > 0) {
            setTimeout(() => {
                close(true);
            }, duration);
        }
    }

    return {
        toast,
        close,
        show
    }
}

window.createToast = createNewToast;
